import React, { useMemo, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';

import SearchableMultiSelect from '../../common/SearchableMultiSelect';
import { useClassNames, SchoolClass } from './sectionParts';
import { segmentedSx } from '../tabLayout';
import type { AdmissionCycle, AssessmentPathway } from '../../../types/admissions';

interface Props {
  open: boolean;
  cycle: AdmissionCycle;
  classes: SchoolClass[];
  /** The pathway being edited; null adds a new one. */
  pathway: AssessmentPathway | null;
  /** New pathways can start as a copy of this one. */
  copyFrom: AssessmentPathway;
  onClose: () => void;
  /** The cycle's pathways as they should be saved, and the name to select. */
  onSave: (pathways: AssessmentPathway[], select: string) => Promise<void>;
}

const blankPathway = (name: string, classIds: string[]): AssessmentPathway => ({
  key: '',
  name,
  classIds,
  stages: { admitCardEnabled: false, testEnabled: false, interviewEnabled: false, customMarksEnabled: false },
  testConfig: { maxMarks: 100, passingMarks: 40, papers: [] },
  interviewConfig: { rounds: [] },
  documentChecklist: [],
  scoringConfig: { customCriteria: [], meritFormula: { components: [], mode: 'weighted-sum', rounding: 2, scaleTo100: true, tieBreakers: [] } },
});

/**
 * Name a pathway and pick its classes. A class already on another pathway
 * says where it is, and picking it moves it here. The first pathway takes
 * every class the others don't, so it only has a name.
 */
const PathwayDialog: React.FC<Props> = ({ open, cycle, classes, pathway, copyFrom, onClose, onSave }) => {
  const pathways = useMemo(() => cycle.pathways || [], [cycle.pathways]);
  const isDefault = !!pathway && pathways[0]?.key === pathway.key;
  const [name, setName] = useState(pathway?.name ?? '');
  const [classIds, setClassIds] = useState<string[]>(pathway?.classIds?.map(String) ?? []);
  const [start, setStart] = useState<'copy' | 'blank'>('copy');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const { nameOf } = useClassNames(classes);

  // Where each class is now, so the picker can say "on Junior".
  const options = useMemo(() => {
    const home = new Map<string, string>();
    pathways.slice(1).forEach(p => (p.classIds || []).forEach(c => home.set(String(c), p.name)));
    return classes.map(c => {
      const on = home.get(c._id);
      const here = pathway && on === pathway.name;
      return { value: c._id, label: nameOf(c._id), secondary: on && !here ? `Now on ${on} — moves here` : undefined };
    });
  }, [classes, pathways, pathway, nameOf]);

  const trimmed = name.trim();
  const clash = pathways.some(p => p.key !== pathway?.key && p.name.trim().toLowerCase() === trimmed.toLowerCase());
  const valid = !!trimmed && !clash && (isDefault || classIds.length > 0);

  const run = async (next: AssessmentPathway[], select: string) => {
    setBusy(true);
    try {
      await onSave(next, select);
      onClose();
    } catch {
      // The save reported its own error; keep the dialog open to fix it.
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    const picked = new Set(classIds);
    // Classes picked here leave whichever pathway had them.
    const others = pathways.map(p => (p.key === pathway?.key || p === pathways[0]
      ? p
      : { ...p, classIds: (p.classIds || []).filter(c => !picked.has(String(c))) }));
    if (pathway) {
      run(others.map(p => (p.key === pathway.key ? { ...p, name: trimmed, classIds: isDefault ? [] : classIds } : p)), trimmed);
    } else {
      const fresh = start === 'copy'
        ? { ...JSON.parse(JSON.stringify(copyFrom)), key: '', name: trimmed, classIds }
        : blankPathway(trimmed, classIds);
      run([...others, fresh], trimmed);
    }
  };

  const remove = () => {
    if (!pathway) return;
    run(pathways.filter(p => p.key !== pathway.key), pathways[0].name);
  };

  const fallback = pathways[0]?.name || 'the main pathway';

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={700}>{pathway ? `Edit ${pathway.name}` : 'New pathway'}</Typography>
        <Typography variant="body2" color="text.secondary">
          {isDefault
            ? 'Every class not on another pathway follows this one.'
            : 'The classes here get their own steps, test, interviews, documents and merit formula.'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 2 }}>
          <TextField
            size="small"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={isDefault ? 'e.g. Main' : 'e.g. Junior'}
            error={clash}
            helperText={clash ? 'Another pathway already has this name.' : ' '}
            autoFocus
            fullWidth
          />
          {!isDefault && (
            <SearchableMultiSelect
              label="Classes"
              values={classIds}
              onChange={setClassIds}
              options={options}
              placeholder="Pick the classes on this pathway"
              helperText={`Classes not on any pathway follow ${fallback}.`}
              fullWidth
            />
          )}
          {!pathway && (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">Start from</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={start}
                onChange={(_, v) => v && setStart(v)}
                sx={segmentedSx}
              >
                <ToggleButton value="copy">A copy of {copyFrom.name}</ToggleButton>
                <ToggleButton value="blank">Nothing set up</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {pathway && !isDefault && (
          <Button
            color="error"
            onClick={() => (confirmDelete ? remove() : setConfirmDelete(true))}
            disabled={busy}
            sx={{ mr: 'auto', textTransform: 'none' }}
          >
            {confirmDelete ? `Remove — its classes follow ${fallback}` : 'Remove pathway'}
          </Button>
        )}
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={busy || !valid}>
          {busy ? 'Saving…' : pathway ? 'Save' : 'Add pathway'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PathwayDialog;
